import React, { useEffect, useState, useCallback } from 'react';
import { Users, Sparkles, Loader2, Trash2, Plus, Swords, FileText, Target } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Panel, PanelLoader, EmptyState, Card, Field, Chip, inputCls } from './shared';
import { toast } from 'sonner';

export default function StrategyPanel({ campaigns = [] }) {
  const [tab, setTab] = useState('personas');
  return (
    <Panel title="Strategy Room" subtitle="Personas, creative briefs and competitive intelligence — the thinking that makes campaigns land." testId="suite-strategy">
      <div className="flex gap-1.5 mb-5 flex-wrap">
        {[['personas', 'Personas'], ['briefs', 'Creative Briefs'], ['competitors', 'Competitors & SWOT']].map(([v, l]) => (
          <Chip key={v} active={tab === v} onClick={() => setTab(v)} testId={`strategy-tab-${v}`}>{l}</Chip>
        ))}
      </div>
      {tab === 'personas' && <Personas />}
      {tab === 'briefs' && <Briefs campaigns={campaigns} />}
      {tab === 'competitors' && <Competitors />}
    </Panel>
  );
}

function Personas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => suiteAPI.listPersonas().then(r => setItems(r.data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!brief.trim()) return toast.error('Describe your product and market');
    setBusy(true);
    try { await suiteAPI.aiPersonas({ brief, count: 3 }); toast.success('Personas created'); load(); }
    catch { toast.error('Generation failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-3">
        <Field label="Who are we selling to?" hint="One or two sentences is enough — AI will build detailed personas.">
          <Textarea value={brief} onChange={e => setBrief(e.target.value)} rows={2} placeholder="Premium streetwear brand selling limited drops to sneaker collectors in US cities" className={inputCls} data-testid="persona-brief" />
        </Field>
        <Button onClick={generate} disabled={busy} className="bg-violet-500 text-white" data-testid="persona-generate">
          {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Generate personas
        </Button>
      </Card>
      {loading ? <PanelLoader /> : items.length === 0 ? <EmptyState icon={Users} text="No personas yet." /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map(p => (
            <Card key={p.id} className="p-4" data-testid={`persona-${p.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-white">{p.name}</p>
                  <p className="text-[11px] text-zinc-500">{p.role} · {p.age_range} · {p.location}</p>
                </div>
                <Button size="sm" variant="ghost" className="text-zinc-600 hover:text-red-400" onClick={() => suiteAPI.deletePersona(p.id).then(load)}><Trash2 size={13} /></Button>
              </div>
              <p className="text-[11px] uppercase tracking-wide text-zinc-500 mt-3">Pains</p>
              {(p.pains || []).slice(0, 3).map((x, i) => <p key={i} className="text-xs text-zinc-400">· {x}</p>)}
              <p className="text-[11px] uppercase tracking-wide text-zinc-500 mt-2">Goals</p>
              {(p.goals || []).slice(0, 3).map((x, i) => <p key={i} className="text-xs text-zinc-400">· {x}</p>)}
              {p.messaging && <p className="text-xs text-[#b8ff00] mt-3 leading-snug">{p.messaging}</p>}
              {(p.channels || []).length > 0 && <p className="text-[11px] text-zinc-500 mt-2">Reach on: {(p.channels || []).join(', ')}</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Briefs({ campaigns }) {
  const [items, setItems] = useState([]);
  const [idea, setIdea] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => suiteAPI.listBriefs().then(r => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!idea.trim()) return toast.error('Describe the campaign idea');
    setBusy(true);
    try { await suiteAPI.aiBrief({ idea, campaign_id: campaignId || null }); toast.success('Brief ready'); setIdea(''); load(); }
    catch { toast.error('Generation failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-3">
        <Field label="Campaign idea">
          <Textarea value={idea} onChange={e => setIdea(e.target.value)} rows={2} placeholder="Holiday gifting push for our jewellery line, focus on last-minute shoppers" className={inputCls} data-testid="brief-idea" />
        </Field>
        <div className="flex gap-3 items-end">
          <Field label="Attach to campaign">
            <select value={campaignId} onChange={e => setCampaignId(e.target.value)} className="bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2">
              <option value="">None</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Button onClick={generate} disabled={busy} className="bg-violet-500 text-white" data-testid="brief-generate">
            {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <FileText size={14} className="mr-1.5" />} Write brief
          </Button>
        </div>
      </Card>
      {items.length === 0 ? <EmptyState icon={FileText} text="No briefs yet." /> : (
        <div className="space-y-3">
          {items.map(b => (
            <Card key={b.id} className="p-5" data-testid={`brief-${b.id}`}>
              <div className="flex justify-between items-start">
                <p className="font-semibold text-white">{b.title}</p>
                <Button size="sm" variant="ghost" className="text-zinc-600 hover:text-red-400" onClick={() => suiteAPI.deleteBrief(b.id).then(load)}><Trash2 size={13} /></Button>
              </div>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 mt-3">
                {[['Background', b.background], ['Objective', b.objective], ['Audience', b.audience], ['Key message', b.key_message], ['Tone', b.tone], ['Timeline', b.timeline], ['Budget', b.budget], ['Mandatories', b.mandatories]].map(([k, v]) => v ? (
                  <div key={k}><p className="text-[11px] uppercase tracking-wide text-zinc-500">{k}</p><p className="text-xs text-zinc-300">{v}</p></div>
                ) : null)}
              </div>
              {(b.deliverables || []).length > 0 && (
                <div className="mt-3"><p className="text-[11px] uppercase tracking-wide text-zinc-500">Deliverables</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">{b.deliverables.map((d, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">{d}</span>)}</div>
                </div>
              )}
              {(b.success_metrics || []).length > 0 && (
                <p className="text-[11px] text-[#b8ff00] mt-3">Success: {b.success_metrics.join(' · ')}</p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Competitors() {
  const [items, setItems] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', website: '', positioning: '', pricing: '' });
  const load = useCallback(() => {
    suiteAPI.listCompetitors().then(r => setItems(r.data));
    suiteAPI.listAnalysis().then(r => setAnalysis(r.data[0]?.result || null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) return toast.error('Competitor name required');
    await suiteAPI.addCompetitor(form);
    setForm({ name: '', website: '', positioning: '', pricing: '' });
    load();
  };

  const analyze = async () => {
    setBusy(true);
    try { const { data } = await suiteAPI.aiCompetitiveAnalysis({}); setAnalysis(data); toast.success('Analysis ready'); }
    catch { toast.error('Analysis failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="grid md:grid-cols-4 gap-3">
          <Field label="Name"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="competitor-name" /></Field>
          <Field label="Website"><Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} className={inputCls} /></Field>
          <Field label="Positioning"><Input value={form.positioning} onChange={e => setForm({ ...form, positioning: e.target.value })} className={inputCls} /></Field>
          <Field label="Pricing"><Input value={form.pricing} onChange={e => setForm({ ...form, pricing: e.target.value })} className={inputCls} /></Field>
        </div>
        <div className="flex gap-2 mt-3">
          <Button onClick={add} className="bg-zinc-800 text-white" data-testid="competitor-add"><Plus size={14} className="mr-1.5" /> Add competitor</Button>
          <Button onClick={analyze} disabled={busy} className="bg-violet-500 text-white" data-testid="competitor-analyze">
            {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Swords size={14} className="mr-1.5" />} Run SWOT & landscape
          </Button>
        </div>
      </Card>

      {items.length > 0 && (
        <div className="grid md:grid-cols-3 gap-3">
          {items.map(c => (
            <Card key={c.id} className="p-4" data-testid={`competitor-${c.id}`}>
              <div className="flex justify-between">
                <p className="font-semibold text-white">{c.name}</p>
                <Button size="sm" variant="ghost" className="text-zinc-600 hover:text-red-400" onClick={() => suiteAPI.deleteCompetitor(c.id).then(load)}><Trash2 size={13} /></Button>
              </div>
              <p className="text-[11px] text-zinc-500">{c.website}</p>
              {c.positioning && <p className="text-xs text-zinc-400 mt-2">{c.positioning}</p>}
              {c.pricing && <p className="text-[11px] text-zinc-500 mt-1">Pricing: {c.pricing}</p>}
            </Card>
          ))}
        </div>
      )}

      {analysis && (
        <div className="grid md:grid-cols-2 gap-3" data-testid="swot-analysis">
          {['strengths', 'weaknesses', 'opportunities', 'threats'].map(k => (
            <Card key={k} className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-[#b8ff00] mb-2">{k}</p>
              {((analysis.swot || {})[k] || []).map((x, i) => <p key={i} className="text-xs text-zinc-300 py-1 border-b border-zinc-800 last:border-0">{x}</p>)}
            </Card>
          ))}
          <Card className="p-4 md:col-span-2">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 flex items-center gap-1.5"><Target size={12} /> Positioning statement</p>
            <p className="text-sm text-white">{analysis.positioning_statement}</p>
            {(analysis.differentiators || []).length > 0 && <>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mt-3 mb-1">Differentiators</p>
              {analysis.differentiators.map((d, i) => <p key={i} className="text-xs text-zinc-300">· {d}</p>)}
            </>}
            {(analysis.competitor_notes || []).map((n, i) => (
              <div key={i} className="mt-3 pt-3 border-t border-zinc-800">
                <p className="text-xs font-semibold text-white">{n.name}</p>
                <p className="text-[11px] text-zinc-500">{n.likely_strategy}</p>
                <p className="text-[11px] text-[#b8ff00] mt-0.5">How to beat them: {n.how_to_beat_them}</p>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}
